import { Effect, Schema } from "effect";
import { HttpApiEndpoint, HttpApiGroup } from "effect/unstable/httpapi";
import { ObservabilityHttpErrorResponses } from "../errors/http.ts";
import { BrowserValidationError } from "../errors/observability.ts";
import { AgenticCacheResult, AgenticTraceContext, AgenticTracePhase } from "../agentic-correlation.ts";
import { BROWSER_OBSERVABILITY_POLICY, BrowserObservationDirection, BrowserObservationKind,
  BrowserObservationLevel, BrowserObservationQuery, BrowserObservationSnapshot } from "../browser-observability-model.ts";

export const BrowserWorkspaceId = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isPattern(/^[a-z0-9][a-z0-9-]{0,63}$/)
).pipe(Schema.brand("browser-workspace")))();
export type BrowserWorkspaceId = typeof BrowserWorkspaceId.Type;

export const BrowserListResponse = /* @__PURE__ */ (() => Schema.Struct({
  workspaceId: BrowserWorkspaceId,
  policy: Schema.Struct({ capture: Schema.Literal(BROWSER_OBSERVABILITY_POLICY.capture),
    retentionMs: Schema.Literal(BROWSER_OBSERVABILITY_POLICY.retentionMs),
    eventLimit: Schema.Literal(BROWSER_OBSERVABILITY_POLICY.eventLimit),
    payloadCharacterLimit: Schema.Literal(BROWSER_OBSERVABILITY_POLICY.payloadCharacterLimit), sanitized: Schema.Literal(true) }),
  ...BrowserObservationSnapshot.fields
}))();
export const BrowserClearResponse = /* @__PURE__ */ (() => Schema.Struct({
  workspaceId: BrowserWorkspaceId, deleted: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))
}))();

const invalid = (message: string) => new BrowserValidationError({ message });
const textParam = Effect.fn("Browser.queryText")(function* (search: URLSearchParams, name: string, maximum: number) {
  const value = search.get(name)?.trim();
  if (!value) return undefined;
  return yield* Schema.decodeUnknownEffect(Schema.String.check(Schema.isMaxLength(maximum)))(value).pipe(
    Effect.mapError(() => invalid(`${name} must be at most ${maximum} characters.`)));
});
const enumParam = Effect.fn("Browser.queryEnum")(function* <A extends string>(
  search: URLSearchParams, name: string, schema: Schema.Literals<readonly A[]>
) {
  const value = search.get(name);
  if (value === null || value === "") return undefined;
  return yield* Schema.decodeUnknownEffect(schema)(value).pipe(
    Effect.mapError(() => invalid(`${name} must be one of: ${schema.literals.join(", ")}.`)));
});
// Browser filters have a single safe-integer diagnostic, including NaN and
// out-of-range values. Number() coercion and first duplicate wins are wire rules.
const numberParam = Effect.fn("Browser.queryNumber")(function* (
  search: URLSearchParams, name: string, minimum: number, maximum: number
) {
  const value = search.get(name);
  if (value === null || value === "") return undefined;
  return yield* Schema.decodeUnknownEffect(Schema.Int.check(Schema.isBetween({ minimum, maximum })))(Number(value)).pipe(
    Effect.mapError(() => invalid(`${name} must be an integer between ${minimum} and ${maximum}.`)));
});
const contextTextKeys = ["changeId", "taskId", "runId", "parentRunId", "attemptId", "worktreeId", "treeId",
  "inputTreeId", "outputTreeId", "candidateId", "checkId", "reviewId", "namespaceId"] as const;

export const browserListFilters = Effect.fn("Browser.listFilters")(function* (search: URLSearchParams) {
  const stationId = yield* textParam(search, "stationId", 64);
  if (stationId) yield* Schema.decodeUnknownEffect(BrowserWorkspaceId)(stationId).pipe(
    Effect.mapError(() => invalid("stationId must be a lowercase slug.")));
  const fields = {
    stationId,
    sessionId: yield* textParam(search, "sessionId", 256),
    bindingName: yield* textParam(search, "bindingName", 128),
    workerName: yield* textParam(search, "workerName", 128),
    direction: yield* enumParam(search, "direction", BrowserObservationDirection),
    kind: yield* enumParam(search, "kind", BrowserObservationKind),
    level: yield* enumParam(search, "level", BrowserObservationLevel),
    category: yield* textParam(search, "category", 160),
    method: yield* textParam(search, "method", 240),
    search: yield* textParam(search, "search", 300)
  };
  const text = yield* Effect.forEach(contextTextKeys, key => textParam(search, key, 256).pipe(
    Effect.map(value => [key, value] as const)), { concurrency: 1 });
  const correlation = yield* Schema.decodeUnknownEffect(AgenticTraceContext)({
    ...Object.fromEntries(text.filter(([, value]) => value !== undefined)),
    fence: yield* numberParam(search, "fence", 1, Number.MAX_SAFE_INTEGER),
    cacheResult: yield* enumParam(search, "cacheResult", AgenticCacheResult),
    eventSequence: yield* numberParam(search, "eventSequence", 0, Number.MAX_SAFE_INTEGER),
    targetRef: yield* textParam(search, "targetRef", 128),
    phase: yield* enumParam(search, "phase", AgenticTracePhase)
  }).pipe(Effect.mapError(error => invalid(error.message)));
  const since = yield* numberParam(search, "since", 0, Number.MAX_SAFE_INTEGER);
  const before = yield* numberParam(search, "before", 0, Number.MAX_SAFE_INTEGER);
  const limit = (yield* numberParam(search, "limit", 1, 500)) ?? 250;
  return yield* Schema.decodeUnknownEffect(BrowserObservationQuery)(Object.fromEntries(
    Object.entries({ ...fields, ...correlation, since, before, limit }).filter(([, value]) => value !== undefined)
  )).pipe(Effect.mapError(error => invalid(error.message)));
});

export const BrowserListQuery = /* @__PURE__ */ (() => {
  const text = Schema.optionalKey(Schema.Union([Schema.String, Schema.Array(Schema.String)]));
  return Schema.Struct({ stationId: text, sessionId: text, bindingName: text, workerName: text,
    direction: text, kind: text, level: text, category: text, method: text, search: text,
    changeId: text, taskId: text, runId: text, parentRunId: text, attemptId: text, worktreeId: text,
    treeId: text, inputTreeId: text, outputTreeId: text, candidateId: text, checkId: text,
    reviewId: text, namespaceId: text, fence: text, cacheResult: text, eventSequence: text,
    targetRef: text, phase: text, since: text, before: text, limit: text });
})();
export const BrowserObservabilityApi = HttpApiGroup.make("browserObservability").add(
  HttpApiEndpoint.get("list", "/api/browser-observability/:workspaceId", {
    params: Schema.Struct({ workspaceId: Schema.String }), query: BrowserListQuery,
    success: BrowserListResponse, error: ObservabilityHttpErrorResponses
  }),
  HttpApiEndpoint.delete("clear", "/api/browser-observability/:workspaceId", {
    params: Schema.Struct({ workspaceId: Schema.String }),
    success: BrowserClearResponse, error: ObservabilityHttpErrorResponses
  })
);
